import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Form, Input, message, Select } from "antd";
import "./index.scss";
import {
  canPerformAction,
  creativeSizeOptions,
  NoticeRequesTypeNames,
  requestTypeOptions,
  StepKeysName,
  StepNames,
} from "../../../RaiseNoticeAdRequest/utils";
import { DatePicker } from "pyxis-ui-kit";
import moment from "moment";
import { CloseCircleOutlined } from "@ant-design/icons";
import EmailForm from "../EmailForm";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  getCitiesByStateAction,
  getQuotationDataAction,
  postQuotationDataAction,
  saveQuoteFormDataAction,
} from "../../../../store/actions/raiseAdRequest";
import { RaiseAdRequestState } from "../../../../store/types/raiseAdRequest";
import { openToast } from "../../../../Toasts";

const { Option } = Select;
const { TextArea } = Input;

const handleDateDisabled = (current: any) => {
  return moment().diff(current, "days") > 0;
};
const permittedRoles = ["admin", "only-approver", "product-admin"];
export const QuoteRequestForm: React.FC = () => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const {
    quoationApprovalFormData,
    states,
    cities,
    userRole,
    lastStepName,
  } = useSelector(
    (state: { raiseAdRequest: RaiseAdRequestState }) => state.raiseAdRequest
  );

  const isStepAccessible = canPerformAction(
    userRole,
    permittedRoles,
    StepNames[0],
    lastStepName,
    quoationApprovalFormData.trashed_at
  );
  const { requestId, selectedProductId } = useParams<{
    requestId: string;
    selectedProductId: string;
  }>();

  const runCount = useRef(0);
  const [isFormValuesChanged, setIsFormValuesChanged] = useState<boolean>(
    false
  );
  const [filedValues, setFieldValues] = useState<any>();
  const [requestType, setRequestType] = useState<string | undefined>();
  const [city, setCity] = useState<string | undefined>();
  const [creativeSize, setCreativeSize] = useState<string | undefined>();
  const [cityDropdownOpen, setCityDropdownOpen] = useState(false);
  const [emailFormValues, setEmailFormValues] = useState({
    to: "",
    cc: "",
    message: "",
  });

  useEffect(() => {
    dispatch(getQuotationDataAction(requestId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isStepAccessible && !!userRole) {
      openToast("info", "You only have view access to this information.");
    }
  }, [isStepAccessible, userRole]);

  // ✅ Populate form when data is available
  useEffect(() => {
    if (quoationApprovalFormData) {
      form.setFieldsValue({
        ...quoationApprovalFormData,
        state: quoationApprovalFormData.state || undefined,
        city: quoationApprovalFormData.city || undefined,
        final_release_date: !!quoationApprovalFormData.final_release_date
          ? moment(quoationApprovalFormData.final_release_date)
          : null,
        length:
          (!!quoationApprovalFormData.length
            ? quoationApprovalFormData.length
            : undefined) ?? undefined,
        width:
          (!!quoationApprovalFormData.width
            ? quoationApprovalFormData.width
            : undefined) ?? undefined,

        emailContent: {
          ...form.getFieldValue("emailContent"),
          ...quoationApprovalFormData.emailContent,
        },
      });

      // if requestType/creativeSize should also update state:
      setRequestType(quoationApprovalFormData.request_type);
      setCreativeSize(quoationApprovalFormData.creative_size);
    }
  }, [
    form,
    quoationApprovalFormData,
    filedValues,
    requestId,
    selectedProductId,
  ]);
  useEffect(() => {
    if (quoationApprovalFormData && runCount.current < 3) {
      setEmailFormValues({
        to: quoationApprovalFormData?.emailContent?.to,
        cc: quoationApprovalFormData?.emailContent?.cc,
        message: quoationApprovalFormData?.emailContent?.message,
      });

      runCount.current += 1; // ✅ increment counter
    }
  }, [quoationApprovalFormData]);

  const handleSelectState = useCallback(
    (value) => {
      const requiredState = states.find((state) => state.name === value);
      dispatch(getCitiesByStateAction(requiredState.id));
      setCity("");
    },
    [dispatch, states]
  );

  const onFinish = useCallback(
    (values: any) => {
      // Destructure otherCity out of values
      const { otherCity, otherRequestType, ...rest } = values;

      let finalValues = { ...rest };

      if (otherCity) {
        finalValues.city = otherCity;
      }

      if (otherRequestType) {
        finalValues.request_type = otherRequestType;
      }

      if (values.creative_size) {
        const match = values.creative_size.match(/(\d+)\s*[xX]\s*(\d+)/);
        if (match) {
          const [, length, width] = match;
          finalValues = {
            ...finalValues,
            length: Number(length),
            width: Number(width),
          };
        }
      }

      if (!!isFormValuesChanged) {
        dispatch(
          postQuotationDataAction({
            id: requestId,
            formData: finalValues,
            stage: "Quotation & Approval",
            nextStepKey: StepKeysName.QuotationAndApproval,
            workspaceId: selectedProductId,
          })
        );
      }
    },
    [
      dispatch,
      emailFormValues,
      isFormValuesChanged,
      requestId,
      selectedProductId,
    ]
  );

  const currentRequestForm = useMemo(() => {
    switch (requestType) {
      case NoticeRequesTypeNames.BranchRelocation:
        return (
          <>
            <div
              style={{
                fontSize: "1.4rem",
                fontWeight: 600,
                color: "#394455",
                marginTop: "2rem",
              }}
            >
              {NoticeRequesTypeNames.BranchRelocation}
            </div>

            <div style={{ display: "flex", gap: "2rem" }}>
              <Form.Item
                label="Branch’s Old Address"
                name={["metadata", "branch_old_address"]}
                style={{ width: "100%" }}
              >
                <TextArea
                  placeholder="Enter Branch’s Old Address"
                  style={{
                    width: "100%",
                  }}
                  readOnly={!isStepAccessible}
                  autoComplete="off"
                />
              </Form.Item>

              <Form.Item
                label="Branch’s New Address "
                name={["metadata", "branch_new_address"]}
                style={{ width: "100%" }}
              >
                <TextArea
                  placeholder="Enter Branch’s New Address "
                  style={{
                    width: "100%",
                  }}
                  readOnly={!isStepAccessible}
                  autoComplete="off"
                />
              </Form.Item>
            </div>
          </>
        );
      case NoticeRequesTypeNames.LockerBreakIn:
        return (
          <>
            <div
              style={{
                fontSize: "1.4rem",
                fontWeight: 600,
                color: "#394455",
                marginTop: "2rem",
              }}
            >
              {NoticeRequesTypeNames.LockerBreakIn}
            </div>

            <Form.Item
              label="Branch Address"
              name={["metadata", "branch_address"]}
              style={{ width: "100%" }}
            >
              <TextArea
                placeholder="Enter Branch Address"
                style={{
                  width: "100%",
                }}
                readOnly={!isStepAccessible}
                autoComplete="off"
              />
            </Form.Item>
          </>
        );

      default:
        break;
    }
  }, [isStepAccessible, requestType]);

  return (
    <>
      <div className="layout-wrapper">
        {/* Scrollable Form */}
        <div className="layout-wrapper__body">
          <Form
            form={form}
            onFinish={onFinish}
            className="custom-form"
            onValuesChange={(changedValues, allValues) => {
              if (!!isStepAccessible) {
                setIsFormValuesChanged(true);
                if (changedValues.emailContent) {
                  const changedKey = Object.keys(changedValues.emailContent)[0];
                  const changedValue = changedValues.emailContent[changedKey];

                  setEmailFormValues((prev) => ({
                    ...prev,
                    [changedKey]: changedValue, // ✅ update only that field
                  }));
                }
                setCreativeSize(allValues.creative_size);
                setRequestType(allValues.request_type);
                setCity(allValues.city);
                // Only dispatch if the changed field is NOT emailContent
                if (Object.keys(changedValues)?.[0] !== "emailContent") {
                  setFieldValues(changedValues);
                  dispatch(saveQuoteFormDataAction(changedValues));
                }
              }
            }}
          >
            <div
              style={{ fontSize: "1.6rem", fontWeight: 600, color: "#394455" }}
            >
              Quote Request Details
            </div>

            <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
              <div style={{ width: "100%" }}></div>

              <Form.Item
                label="Request Name"
                name="request_name"
                rules={[
                  { required: true, message: "Please enter the request name" },
                  {
                    pattern: /^[a-zA-Z0-9 ]+$/,
                    message: "Special characters are not allowed",
                  },
                ]}
              >
                <Input
                  placeholder="Enter request name"
                  readOnly={!isStepAccessible}
                  autoComplete="off"
                  onKeyPress={(e) => {
                    if (!/[a-zA-Z0-9 ]/.test(e.key)) {
                      e.preventDefault(); // ⛔ Block special characters
                    }
                  }}
                />
              </Form.Item>

              <Form.Item
                label="Request Type"
                name="request_type"
                rules={[
                  { required: true, message: "Please select a request type" },
                ]}
              >
                <Select placeholder="Select type" disabled={!isStepAccessible}>
                  {requestTypeOptions.map((option) => (
                    <Option key={option.value} value={option.label}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label="State"
                name="state"
                rules={[{ required: true, message: "Please select state" }]}
              >
                <Select
                  placeholder="Select state"
                  onChange={handleSelectState}
                  disabled={!isStepAccessible}
                >
                  {states.map(({ id, name }) => (
                    <Option key={`states-${id}`} value={name}>
                      {name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label="City"
                name="city"
                rules={[{ required: true, message: "Please select city" }]}
              >
                <Select
                  placeholder="Select city"
                  open={cityDropdownOpen}
                  disabled={
                    !isStepAccessible || !quoationApprovalFormData.state
                  }
                  onDropdownVisibleChange={(open) => setCityDropdownOpen(open)}
                >
                  {cities.map(({ id, name }) => (
                    <Option key={`cities-${id}`} value={name}>
                      {name}
                    </Option>
                  ))}
                  <Option value="other">Other</Option>
                </Select>
              </Form.Item>

              {city === "other" ? (
                <Form.Item
                  label="Other City Name"
                  name="otherCity"
                  rules={[
                    { required: true, message: "Please enter the city name" },
                    {
                      pattern: /^[A-Za-z\s]+$/, // ✅ allows only letters & spaces
                      message: "City name should only contain letters",
                    },
                    {
                      pattern: /^[a-zA-Z0-9 ]+$/,
                      message: "Special characters are not allowed",
                    },
                  ]}
                >
                  <Input
                    placeholder="Enter city name"
                    readOnly={!isStepAccessible}
                    autoComplete="off"
                  />
                </Form.Item>
              ) : (
                <></>
              )}

              {requestType === "Other" ? (
                <Form.Item
                  label="Specify Request Type"
                  name="otherRequestType"
                  rules={[
                    {
                      required: true,
                      message: "Please enter the Request Type",
                    },
                    {
                      pattern: /^[A-Za-z\s]+$/, // ✅ allows only letters & spaces
                      message: "City name should only contain letters",
                    },
                    {
                      pattern: /^[a-zA-Z0-9 ]+$/,
                      message: "Special characters are not allowed",
                    },
                  ]}
                >
                  <Input
                    placeholder="Enter type of request"
                    readOnly={!isStepAccessible}
                    autoComplete="off"
                  />
                </Form.Item>
              ) : (
                <></>
              )}
            </div>

            {currentRequestForm}

            <div
              style={{
                display: "flex",
                gap: "3rem",
                flexWrap: "wrap",
                marginTop: "2rem",
              }}
            >
              <Form.Item
                label="Final Release Date of Ad"
                name="final_release_date"
                rules={[
                  {
                    required: true,
                    message: "Please select the final release date",
                  },
                ]}
              >
                <DatePicker
                  format="DD-MM-YYYY"
                  placeholder="Select date"
                  disabledDate={handleDateDisabled}
                  style={{
                    padding: "0.6rem 1rem",
                    width: "26rem",
                    borderRadius: "6px",
                  }}
                  disabled={!isStepAccessible}
                />
              </Form.Item>

              <Form.Item
                label="Branch Code"
                name="branch_code"
                rules={[
                  { required: true, message: "Please enter the branch code." },
                  {
                    pattern: /^[a-zA-Z0-9 ]+$/,
                    message: "Special characters are not allowed",
                  },
                ]}
              >
                <Input
                  placeholder="Add Branch Code"
                  readOnly={!isStepAccessible}
                  autoComplete="off"
                />
              </Form.Item>

              <Form.Item
                label="Cost Code"
                name="cost_code"
                rules={[
                  { required: true, message: "Please enter the Cost Code." },
                  {
                    pattern: /^[0-9]+$/,
                    message: (
                      <>
                        <CloseCircleOutlined
                          style={{ color: "red", marginRight: 4 }}
                        />
                        Enter numerical value only
                      </>
                    ),
                  },
                ]}
              >
                <Input
                  placeholder="Add Cost Code"
                  type="text"
                  readOnly={!isStepAccessible}
                  autoComplete="off"
                />
              </Form.Item>

              <Form.Item
                label="GL Code"
                name="gl_code"
                rules={[
                  {
                    pattern: /^[a-zA-Z0-9 ]+$/,
                    message: "Special characters are not allowed",
                  },
                ]}
              >
                <Input
                  placeholder="Add GL Code"
                  readOnly={!isStepAccessible}
                  autoComplete="off"
                />
              </Form.Item>
            </div>

            <div
              style={{
                display: "flex",
                gap: "3rem",
                flexWrap: "wrap",
                marginTop: "2rem",
              }}
            >
              <Form.Item label="Creative Size" name="creative_size">
                <Select placeholder="Select Size" disabled={!isStepAccessible}>
                  {creativeSizeOptions.map((option) => (
                    <Option key={option.value} value={option.label}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              {creativeSize === "Other" ? (
                <>
                  <Form.Item
                    label="Width (cm)"
                    name="length"
                    rules={[
                      { required: true, message: "Width is required" },
                      {
                        pattern: /^[0-9]+$/,
                        message: "Enter numerical value only",
                      },
                    ]}
                  >
                    <Input
                      placeholder="Add Width"
                      readOnly={!isStepAccessible}
                      autoComplete="off"
                      onKeyPress={(e) => {
                        if (!/[0-9]/.test(e.key)) {
                          e.preventDefault(); // block non-numeric characters
                        }
                      }}
                    />
                  </Form.Item>

                  <Form.Item
                    label="Height (cm)"
                    name="width"
                    rules={[
                      {
                        required: true,
                        pattern: /^[0-9]+$/,
                        message: (
                          <>
                            <CloseCircleOutlined
                              style={{ color: "red", marginRight: 4 }}
                            />
                            Enter numerical value only
                          </>
                        ),
                      },
                    ]}
                  >
                    <Input
                      placeholder="Add Height"
                      type="text"
                      autoComplete="off"
                      onKeyPress={(e) => {
                        if (!/[0-9]/.test(e.key)) {
                          e.preventDefault(); // block non-numeric characters
                        }
                      }}
                    />
                  </Form.Item>
                </>
              ) : (
                <></>
              )}
            </div>

            <div
              style={{
                display: "flex",
                gap: "3rem",
                flexWrap: "wrap",
                marginTop: "2rem",
              }}
            >
              <Form.Item
                label="Please list down the email id and names of all stake holders"
                name="specific_requirements"
                style={{ width: "100%" }}
              >
                <TextArea
                  placeholder="Please list down the email id and names of all stake holders"
                  style={{
                    width: "100%",
                  }}
                  autoComplete="off"
                  readOnly={!isStepAccessible}
                />
              </Form.Item>
            </div>
          </Form>
        </div>
        {!!isStepAccessible ? (
          <div className="layout-wrapper__footer">
            <button
              className="btn btn--purple"
              disabled={
                !isFormValuesChanged || !emailFormValues?.to?.trim().length
              }
              onClick={() => form.submit()}
            >
              Raise a Request
            </button>
          </div>
        ) : (
          <></>
        )}
      </div>
    </>
  );
};
